import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { auth } from './auth';
import { Task, TaskFormData } from '../types/task';
import { COLLECTIONS } from '../constants';
import { getErrorMessage } from '../utils/errorHandling';
import { convertFirestoreTimestamp, convertToFirestoreTimestamp } from '../utils/dateUtils';

// Initialize Firestore
const db = getFirestore();

// Helper to get current user ID
const getCurrentUserId = () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Please sign in to continue. You need to be authenticated to access your tasks.');
  }
  return user.uid;
};

// Task operations
export const firestoreService = {
  // Create a new task
  async createTask(taskData: TaskFormData): Promise<Task> {
    try {
      const userId = taskData.userId || getCurrentUserId();
      // Determine status
      let status: 'pending_approval' | 'approved' | 'rejected' | 'pending_verification' | 'verified' | 'completed' = 'completed';
      if ((!taskData.userId || taskData.userId === userId) && taskData.credits > 0) {
        status = 'pending_approval';
      }
      const taskWithMetadata = {
        ...taskData,
        status, // Added status here
        dueDate: convertToFirestoreTimestamp(taskData.dueDate),
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.TASKS), taskWithMetadata);

      return {
        id: docRef.id,
        ...taskData,
        status, // Added status here
        userId,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error creating task:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get all tasks for current user
  async getTasks(): Promise<Task[]> {
    try {
      const userId = getCurrentUserId();
      const q = query(
        collection(db, COLLECTIONS.TASKS),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          dueDate: convertFirestoreTimestamp(data.dueDate),
          createdAt: convertFirestoreTimestamp(data.createdAt),
          updatedAt: convertFirestoreTimestamp(data.updatedAt),
          completedAt: data.completedAt ? convertFirestoreTimestamp(data.completedAt) : undefined,
          status: data.status || 'completed' // Default to completed for backward compatibility
        };
      }) as Task[];
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Update a task
  async updateTask(taskId: string, updates: Partial<Task>): Promise<void> {
    try {
      const userId = getCurrentUserId();
      const taskRef = doc(db, COLLECTIONS.TASKS, taskId);
      
      await updateDoc(taskRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating task:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Delete a task
  async deleteTask(taskId: string): Promise<void> {
    try {
      const userId = getCurrentUserId();
      const taskRef = doc(db, COLLECTIONS.TASKS, taskId);
      await deleteDoc(taskRef);
    } catch (error) {
      console.error('Error deleting task:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Complete a task
  async completeTask(taskId: string): Promise<void> {
    try {
      // Get the task first to check if it has credits
      const taskRef = doc(db, COLLECTIONS.TASKS, taskId);
      const taskDoc = await getDoc(taskRef);
      
      if (!taskDoc.exists()) {
        throw new Error('Task not found.');
      }
      
      const taskData = taskDoc.data();
      
      // If task has credits, require verification
      if (taskData.credits > 0) {
        await updateDoc(taskRef, { 
          status: 'pending_verification',
          updatedAt: serverTimestamp()
        });
      } else {
        // No credits, complete immediately
        await updateDoc(taskRef, { 
          status: 'completed',
          completedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error completing task:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get user stats
  async getUserStats() {
    const userId = getCurrentUserId();
    const q = query(
      collection(db, COLLECTIONS.USER_STATS),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      // Create default stats if none exist
      const defaultStats = {
        userId,
        totalTasks: 0,
        completedTasks: 0,
        currentStreak: 0,
        longestStreak: 0,
        lastCompletedDate: null,
        earnedCredits: 300, // New users start with 300 credits
        availableCredits: 300, // New users start with 300 credits
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.USER_STATS), defaultStats);
      return { id: docRef.id, ...defaultStats };
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      lastCompletedDate: doc.data().lastCompletedDate?.toDate() || null,
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    };
  },

  // Update user stats
  async updateUserStats(stats: any): Promise<void> {
    const userId = getCurrentUserId();
    const q = query(
      collection(db, COLLECTIONS.USER_STATS),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      // Create new stats document
      await addDoc(collection(db, COLLECTIONS.USER_STATS), {
        ...stats,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      // Update existing stats document
      const docRef = querySnapshot.docs[0].ref;
      await updateDoc(docRef, {
        ...stats,
        updatedAt: serverTimestamp()
      });
    }
  },

  // Get pending missions for credit approvers
  async getPendingMissionsForApprovers(): Promise<Task[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Please sign in to continue.');
      }

      // Get all users for whom the current user is an approver
      // Use the same logic as getUsersIApprove in userService
      const approverQuery = query(
        collection(db, COLLECTIONS.CREDIT_APPROVERS),
        where('approverUid', '==', user.uid),
        where('status', '==', 'accepted')
      );
      const approverSnapshot = await getDocs(approverQuery);
      
      if (approverSnapshot.empty) {
        console.log('No accepted approver relationships found for user:', user.uid);
        return [];
      }

      // Get user IDs that this user approves for
      const approvedUserIds = approverSnapshot.docs.map(doc => doc.data().userId);
      console.log('Users this person approves for:', approvedUserIds);
      
      // Get all pending missions from these users
      const pendingMissions: Task[] = [];
      
      for (const userId of approvedUserIds) {
        const missionQuery = query(
          collection(db, COLLECTIONS.TASKS),
          where('userId', '==', userId),
          where('status', '==', 'pending_approval')
        );
        const missionSnapshot = await getDocs(missionQuery);
        
        console.log(`Found ${missionSnapshot.docs.length} pending missions for user ${userId}`);
        
        const missions = missionSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dueDate: convertFirestoreTimestamp(data.dueDate),
            createdAt: convertFirestoreTimestamp(data.createdAt),
            updatedAt: convertFirestoreTimestamp(data.updatedAt),
            completedAt: data.completedAt ? convertFirestoreTimestamp(data.completedAt) : undefined
          };
        }) as Task[];
        
        pendingMissions.push(...missions);
      }
      
      console.log(`Total pending missions found: ${pendingMissions.length}`);
      return pendingMissions;
    } catch (error) {
      console.error('Error fetching pending missions for approvers:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Approve a pending mission
  async approveMission(missionId: string, approvedCredits: number): Promise<void> {
    try {
      const missionRef = doc(db, COLLECTIONS.TASKS, missionId);
      
      // Get the task data to get the user ID
      const taskDoc = await getDoc(missionRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found.');
      }
      
      const taskData = taskDoc.data();
      const userId = taskData.userId;
      
      // Update task status
      await updateDoc(missionRef, {
        status: 'approved',
        credits: approvedCredits,
        updatedAt: serverTimestamp()
      });
      
      // Add credits to user stats
      if (approvedCredits > 0) {
        const userStatsQuery = query(
          collection(db, COLLECTIONS.USER_STATS),
          where('userId', '==', userId)
        );
        const userStatsSnapshot = await getDocs(userStatsQuery);
        
        if (!userStatsSnapshot.empty) {
          const userStatsRef = userStatsSnapshot.docs[0].ref;
          const currentStats = userStatsSnapshot.docs[0].data();
          
                      await updateDoc(userStatsRef, {
              earnedCredits: (currentStats.earnedCredits || 0) + approvedCredits,
              availableCredits: (currentStats.availableCredits || 0) + approvedCredits,
              updatedAt: serverTimestamp()
            });
        }
      }
    } catch (error) {
      console.error('Error approving mission:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Reject a pending mission
  async rejectMission(missionId: string, rejectionReason?: string): Promise<void> {
    try {
      const missionRef = doc(db, COLLECTIONS.TASKS, missionId);
      await updateDoc(missionRef, {
        status: 'rejected',
        rejectionReason: rejectionReason || '',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error rejecting mission:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Get pending mission verifications for credit approvers
  async getPendingMissionVerifications(): Promise<Task[]> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Please sign in to continue.');
      }

      // Get all users for whom the current user is an approver
      const approverQuery = query(
        collection(db, COLLECTIONS.CREDIT_APPROVERS),
        where('approverUid', '==', user.uid),
        where('status', '==', 'accepted')
      );
      const approverSnapshot = await getDocs(approverQuery);
      
      if (approverSnapshot.empty) {
        return [];
      }

      // Get user IDs that this user approves for
      const approvedUserIds = approverSnapshot.docs.map(doc => doc.data().userId);
      
      // Get all pending verifications from these users
      const pendingVerifications: Task[] = [];
      
      for (const userId of approvedUserIds) {
        const verificationQuery = query(
          collection(db, COLLECTIONS.TASKS),
          where('userId', '==', userId),
          where('status', '==', 'pending_verification')
        );
        const verificationSnapshot = await getDocs(verificationQuery);
        
        const verifications = verificationSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dueDate: convertFirestoreTimestamp(data.dueDate),
            createdAt: convertFirestoreTimestamp(data.createdAt),
            updatedAt: convertFirestoreTimestamp(data.updatedAt),
            completedAt: data.completedAt ? convertFirestoreTimestamp(data.completedAt) : undefined
          };
        }) as Task[];
        
        pendingVerifications.push(...verifications);
      }
      
      return pendingVerifications;
    } catch (error) {
      console.error('Error fetching pending mission verifications:', error);
      throw new Error(getErrorMessage(error));
    }
  },

  // Verify a mission completion
  async verifyMissionCompletion(missionId: string, verified: boolean, reason?: string): Promise<void> {
    try {
      const missionRef = doc(db, COLLECTIONS.TASKS, missionId);
      
      if (verified) {
        // Get the task data to check credits
        const taskDoc = await getDoc(missionRef);
        if (!taskDoc.exists()) {
          throw new Error('Task not found.');
        }
        
        const taskData = taskDoc.data();
        const credits = taskData.credits || 0;
        
        // Update task status
        await updateDoc(missionRef, {
          status: 'verified',
          completedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        // Add credits to user stats
        if (credits > 0) {
          const userId = taskData.userId;
          console.log('Adding credits to user stats:', { userId, credits });
          
          const userStatsQuery = query(
            collection(db, COLLECTIONS.USER_STATS),
            where('userId', '==', userId)
          );
          const userStatsSnapshot = await getDocs(userStatsQuery);
          
          if (!userStatsSnapshot.empty) {
            const userStatsRef = userStatsSnapshot.docs[0].ref;
            const currentStats = userStatsSnapshot.docs[0].data();
            console.log('Current user stats:', currentStats);
            
            const updatedStats = {
              earnedCredits: (currentStats.earnedCredits || 0) + credits,
              availableCredits: (currentStats.availableCredits || 0) + credits,
              updatedAt: serverTimestamp()
            };
            
            console.log('Updating user stats with:', updatedStats);
            await updateDoc(userStatsRef, updatedStats);
            console.log('Credits added successfully');
          } else {
            console.log('User stats document not found for user:', userId);
            // Create user stats document if it doesn't exist
            const defaultStats = {
              userId,
              totalTasks: 0,
              completedTasks: 0,
              currentStreak: 0,
              longestStreak: 0,
              lastCompletedDate: null,
              earnedCredits: credits,
              availableCredits: credits,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };
            
            console.log('Creating new user stats document:', defaultStats);
            await addDoc(collection(db, COLLECTIONS.USER_STATS), defaultStats);
            console.log('User stats document created with credits');
          }
        }
      } else {
        await updateDoc(missionRef, {
          status: 'rejected',
          verificationReason: reason || '',
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error verifying mission completion:', error);
      throw new Error(getErrorMessage(error));
    }
  }
}; 